import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react'
import Picker from '@emoji-mart/react'
import { dropLast, includes } from 'ramda'
import React, {
  ForwardedRef,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import TextareaAutosize from 'react-textarea-autosize'
import { useDebounce } from 'react-use'
import styled from 'styled-components'
import iconCloseXDark900 from '~/assets/icons-light-theme/icon-close-x-dark900.svg'
import activeEmojiIcon from '~/assets/icons-light-theme/icon-emoji-blue900.svg'
import iconEmojiBlue900 from '~/assets/icons-light-theme/icon-emoji-blue900.svg'
import iconEmojiDark700 from '~/assets/icons-light-theme/icon-emoji-dark700.svg'
import iconEmojiDark900 from '~/assets/icons-light-theme/icon-emoji-dark900.svg'
import activeMMSIcon from '~/assets/icons-light-theme/icon-MMS-blue900.svg'
import iconMmsBlue900 from '~/assets/icons-light-theme/icon-MMS-blue900.svg'
import iconMmsDark700 from '~/assets/icons-light-theme/icon-MMS-dark700.svg'
import iconMmsDark900 from '~/assets/icons-light-theme/icon-MMS-dark900.svg'
import iconSendTextBlue900 from '~/assets/icons-light-theme/icon-sendText-blue900.svg'
import iconSendTextDark700 from '~/assets/icons-light-theme/icon-sendText-dark700.svg'
import activeSnippetIcon from '~/assets/icons-light-theme/icon-snippet-blue900.svg'
import iconSnippetsBlue900 from '~/assets/icons-light-theme/icon-snippet-blue900.svg'
import iconSnippetsDark700 from '~/assets/icons-light-theme/icon-snippet-dark700.svg'
import iconSnippetsDark900 from '~/assets/icons-light-theme/icon-snippet-dark900.svg'
import iconFaxDisabled from '~/assets/icons/icon-fax-disabled.png'
import iconSendFullGray from '~/assets/icons/icon-send-full-gray.svg'
import { useUserState } from '~/atoms/UserState'
import CharacterCount from '~/components/CharacterCounter'
import { easeInOutTransition, focusedInput } from '~/components/CommonStyles'
import { ImageButton, Text } from '~/components/core'
import { FontStyle } from '~/components/core/Text'
import { ImageIcon } from '~/components/ImageIcon'
import SnippetPopup from '~/components/SnippetPopup'
import { ImageTypes } from '~/constants/config'
import { dimensions, light, spacing } from '~/constants/theme'
import useMaxCharacterCount from '~/hooks/useMaxCharacterCount'
import { useSearchSnippets } from '~/queries/snippetQueries'
import { desktop } from '~/styles'
import { EmojiData } from '~/types/EmojiMartTypes'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { SettingsType } from '~/types/Settings'

import { MessageSnippet } from '@mangovoice/types'

const InputRootWrapper = styled.div`
  width: 100%;
`

const InputStatusWrapper = styled.div`
  width: 100%;
  height: 25px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const InputFileWrapper = styled.div`
  width: 144px;
  height: 56px;
  padding: ${spacing.spacingLG};
  display: flex;
  align-items: center;
  border-radius: ${dimensions.borderRadiusSM};
  background-color: ${light.gray400};

  > p {
    margin-left: ${spacing.spacingLG};
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const InputImageRootWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`

const InputImageWrapper = styled.div`
  height: 66px;
  width: fit-content;
  padding-right: ${spacing.spacingLG};
  padding-top: ${spacing.spacingLG};
  margin-bottom: ${spacing.spacingLG};
  background-color: ${light.gray100};
  position: relative;
  ${easeInOutTransition}

  > img {
    border-radius: ${dimensions.borderRadiusSM};
  }

  > div {
    display: none;
    position: absolute;
    top: 0px;
    right: 0px;
    z-index: 10;
  }

  > ${InputFileWrapper} {
    display: flex;
    position: unset;
  }

  &:hover {
    > div {
      display: flex;
    }
  }
`

const InputImage = styled.img`
  height: 100%;
  width: auto;
`

interface InputWrapperProps {
  opened?: boolean
  hasExceededMaxCount?: boolean
  isNewThread?: boolean
}

const InputWrapper = styled.div<InputWrapperProps>`
  width: 100%;
  padding: 0 0 0 ${spacing.spacingLG};
  border-radius: ${dimensions.borderRadiusSM};

  ${({ isNewThread }) => (isNewThread ? `min-height: 100px;` : '')}
  border: 1px solid
    ${({ hasExceededMaxCount }) =>
    hasExceededMaxCount ? light.red200 : light.dark700};
  background-color: ${light.gray100};
  ${(props) =>
    props.opened
      ? 'border-top-right-radius: 0; border-top-left-radius: 0;'
      : ''}
  :focus-within {
    ${focusedInput}
  }
  ${easeInOutTransition};
`

const InputFieldWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`

const InputBox = styled(TextareaAutosize)`
  width: 100%;
  padding: ${spacing.spacingMD} 0;
  font-size: 13px;
  line-height: 17px;
  border: none;
  resize: none;
  background-color: ${light.gray100};
  ${easeInOutTransition};

  &:focus {
    outline: none;
    outline-offset: 0;
    background-color: ${light.transparent};
  }

  &::-webkit-scrollbar {
    width: ${desktop.scrollbar};
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #acacac40;
    border-radius: ${desktop.scrollbar};
  }
`

const EmojiTriggerWrapper = styled.button`
  border: none;
  padding: 0;
  background-color: transparent;
`

const ButtonsWrapper = styled.div`
  display: flex;
`

interface MessageInputProps {
  disabled?: boolean
  file?: File
  fileURL?: string | null
  opened?: boolean
  text?: string
  onChange?: (value: string | undefined) => void
  onKeyDown?: (e: KeyboardEvent) => void
  onOpen?: () => void
  onRemove?: () => void
  onSelect?: (e: EmojiData) => void
  onSend?: () => void
  isNewThread?: boolean
  snippetsDisabled?: boolean
  placeholder?: string
}

const MessageInput = React.forwardRef(
  (props: MessageInputProps, ref: ForwardedRef<HTMLTextAreaElement>) => {
    const {
      disabled,
      file,
      fileURL,
      opened,
      text,
      onChange,
      onKeyDown,
      onOpen,
      onRemove,
      onSelect,
      onSend,
      isNewThread,
      snippetsDisabled,
      placeholder = 'Send a message here',
    } = props

    const forwardedRef = ref as React.RefObject<HTMLElement> | undefined | null

    const { pbxId } = useUserState()
    const [openSnippet, setOpenSnippet] = useState(false)
    const [position, setPosition] = useState(0)
    const [search, setSearch] = useState('')
    const [debounceSearch, setDebounceSearch] = useState('')
    const [oldText, setOldText] = useState('')
    const listRef = useRef<HTMLDivElement | undefined>()
    const [snippets, setSnippets] = useState<MessageSnippet[]>([])
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { hasExceededMaxCount, count } = useMaxCharacterCount(
      text?.length ?? 0,
    )

    const handleSetOpenSnippet = (value) => {
      setOpenSnippet(value && !snippetsDisabled)
    }

    useSearchSnippets(
      search,
      (data) => {
        setSnippets(data.results.filter((s) => !s.pbx || s.pbx.id === pbxId))
        handleSetOpenSnippet(true)
      },
      {
        refetchOnWindowFocus: true,
        enabled: openSnippet,
      },
    )

    const snippetsLen = snippets.length
    const fileType = file?.type
    const fileName = file?.name

    const [, cancel] = useDebounce(
      () => {
        window.gtag('event', 'snippets_searched', { search: debounceSearch })
        setSearch(debounceSearch)
      },
      500,
      [debounceSearch],
    )

    useEffect(() => {
      if (openSnippet) {
        setPosition(0)
        setOldText(dropLast(1, text ?? ''))
      }
    }, [openSnippet])

    useEffect(() => {
      if (text === oldText) {
        handleSetOpenSnippet(false)
      }
    }, [text, oldText])

    const handleChangeText = (e) => {
      onChange?.(e.target.value)
    }

    const handleSelectSnippet = useCallback(
      (snippet: MessageSnippet) => {
        const { id, body, name, scope } = snippet
        onChange?.(`${oldText} ${body}`)
        setDebounceSearch('')
        handleSetOpenSnippet(false)
        setTimeout(() => {
          forwardedRef?.current?.focus()
        }, 100)
        window.gtag('event', 'snippet_selected', {
          id: id,
          name: name,
          body: body,
          scope: scope,
        })
      },
      [oldText, onChange, ref],
    )

    useEffect(() => {
      //prettier-ignore
      const firstElementChild = listRef.current?.firstElementChild as HTMLElement | undefined
      firstElementChild?.focus()
      cancel()
    }, [])

    useEffect(() => {
      const handleKeydown = (e: KeyboardEvent) => {
        if (openSnippet) {
          //prettier-ignore
          const newPositionChild = listRef.current?.children[position] as HTMLElement

          switch (e.key) {
            case 'Enter':
            case 'Tab': {
              if (position < snippetsLen) {
                const selectedSnippet = snippets[position]

                e.preventDefault()
                handleSelectSnippet(selectedSnippet)
              }
            }
            // eslint-disable-next-line no-fallthrough
            case 'down':
            case 'ArrowDown': {
              if (position === snippets.length - 1 || snippets.length === 0)
                return
              const newPosition = position + 1
              setPosition(newPosition)
              newPositionChild.focus()
              break
            }
            case 'up':
            case 'ArrowUp': {
              if (position === 0) return
              const newPosition = position - 1
              setPosition(newPosition)
              break
            }
            case 'Backspace':
              if (debounceSearch !== '') {
                setDebounceSearch(dropLast(1, debounceSearch))
              }
              break
            case 'Escape':
              handleSetOpenSnippet(false)
              break
            default:
              onKeyDown?.(e)
          }

          return
        }
        onKeyDown?.(e)
      }

      addEventListener('keydown', handleKeydown)
      return () => removeEventListener('keydown', handleKeydown)
    }, [
      openSnippet,
      snippets,
      snippetsLen,
      debounceSearch,
      position,
      handleSelectSnippet,
      onKeyDown,
    ])

    const handleKeyPress = (e) => {
      if (openSnippet) {
        setDebounceSearch(`${debounceSearch}${String.fromCharCode(e.charCode)}`)
      } else if (e.charCode === 35) {
        const last = text?.substring(text.length - 1, text.length)
        handleSetOpenSnippet(last !== '#')
        window.gtag('event', 'snippet_drawer_key_press', {})
      }
    }

    const handleSelectEmoji = (emoji: EmojiData) => {
      if (onSelect) onSelect(emoji)
      window.gtag('event', 'emoji_selected', {
        id: emoji.id,
        emoji: emoji.native,
      })
    }

    const handleOpenSnippet = () => {
      if (snippetsDisabled) {
        return
      }

      if (!openSnippet) {
        window.gtag('event', 'snippet_drawer_clicked', {})
        const last = text?.substring(text.length - 1, text.length)
        if (!text || text.length === 0) {
          onChange?.('#')
        } else if (last !== '#') {
          onChange?.(`${text} #`)
        }
        setDebounceSearch('')

        if (ref) {
          forwardedRef?.current?.focus()
        }
      }
      handleSetOpenSnippet(!openSnippet)
    }

    const handleNavigation = () => {
      onChange?.(oldText)

      window.gtag('event', 'open_snippet_settings', {})

      navigate(`${ApplicationRoutes.SETTINGS}/${SettingsType.SNIPPETS}`)
    }

    return (
      <InputRootWrapper>
        {!isNewThread ? (
          <InputStatusWrapper>
            {includes('send-message', status) && !!file && (
              <Text
                fontStyle={FontStyle.ExTiny}
                text={
                  status === 'pending-send-message'
                    ? t('messageInput.statusMessage.uploading')
                    : t('messageInput.statusMessage.failedToUpload')
                }
                color={
                  status === 'pending-send-message'
                    ? light.primary
                    : light.red200
                }
              />
            )}
          </InputStatusWrapper>
        ) : null}

        <div style={{ position: 'relative' }}>
          {!snippetsDisabled && openSnippet && snippetsLen > 0 && (
            <SnippetPopup
              listRef={listRef as RefObject<HTMLDivElement>}
              data={snippets}
              position={position}
              onHover={(idx) => setPosition(idx)}
              onNavigate={handleNavigation}
              onSelect={(snippet) => handleSelectSnippet(snippet)}
              onShouldClose={() => handleSetOpenSnippet(false)}
            />
          )}
          <InputWrapper
            hasExceededMaxCount={hasExceededMaxCount}
            isNewThread={isNewThread}
            opened={openSnippet}
          >
            <InputFieldWrapper>
              <InputBox
                ref={ref}
                placeholder={placeholder}
                value={text}
                maxRows={5}
                onChange={handleChangeText}
                onKeyPress={handleKeyPress}
                autoFocus
              />
              {!fileURL && (
                <ButtonsWrapper>
                  <ImageButton
                    icon={opened ? activeMMSIcon : iconMmsDark700}
                    hoverIcon={opened ? activeMMSIcon : iconMmsDark900}
                    activeIcon={opened ? activeMMSIcon : iconMmsBlue900}
                    width="32px"
                    height="34px"
                    imgWidth={22}
                    imgHeight={22}
                    tooltipText={t('messageInput.attachMedia')}
                    backColor={light.transparent}
                    activeColor={light.transparent}
                    hoveredColor={light.transparent}
                    onClick={onOpen}
                  />
                  <ImageButton
                    icon={openSnippet ? activeSnippetIcon : iconSnippetsDark700}
                    hoverIcon={
                      openSnippet ? activeSnippetIcon : iconSnippetsDark900
                    }
                    activeIcon={
                      openSnippet ? activeSnippetIcon : iconSnippetsBlue900
                    }
                    width="32px"
                    height="34px"
                    imgWidth={22}
                    imgHeight={22}
                    tooltipText={t('messageInput.addSnippet')}
                    backColor={light.transparent}
                    activeColor={light.transparent}
                    hoveredColor={light.transparent}
                    onClick={handleOpenSnippet}
                  />
                  <Popover isLazy placement="end-end">
                    {({ isOpen }) => (
                      <>
                        <PopoverTrigger>
                          <EmojiTriggerWrapper>
                            <ImageButton
                              icon={isOpen ? activeEmojiIcon : iconEmojiDark700}
                              hoverIcon={
                                isOpen ? activeEmojiIcon : iconEmojiDark900
                              }
                              activeIcon={
                                isOpen ? activeEmojiIcon : iconEmojiBlue900
                              }
                              width="32px"
                              height="34px"
                              imgWidth={23}
                              imgHeight={23}
                              tooltipText={t('messageInput.emojis')}
                              backColor={light.transparent}
                              activeColor={light.transparent}
                              hoveredColor={light.transparent}
                            />
                          </EmojiTriggerWrapper>
                        </PopoverTrigger>
                        <PopoverContent>
                          <Picker
                            autoFocus={true}
                            emojiTooltip
                            theme={'light'}
                            sheetSize={32}
                            showPreview={false}
                            showSkinTones={false}
                            onEmojiSelect={handleSelectEmoji}
                          />
                        </PopoverContent>
                      </>
                    )}
                  </Popover>
                  {!isNewThread ? (
                    <ImageButton
                      disabled={disabled ?? hasExceededMaxCount}
                      icon={iconSendTextBlue900}
                      disabledIcon={iconSendTextDark700}
                      width="32px"
                      height="34px"
                      imgWidth={22}
                      imgHeight={22}
                      backColor={light.transparent}
                      activeColor={light.transparent}
                      hoveredColor={light.transparent}
                      disabledColor={light.transparent}
                      onClick={onSend}
                    />
                  ) : null}
                </ButtonsWrapper>
              )}
            </InputFieldWrapper>
            {fileURL && (
              <InputImageRootWrapper>
                <InputImageWrapper>
                  {ImageTypes.includes(fileType ?? '') ? (
                    <InputImage src={fileURL} />
                  ) : (
                    <InputFileWrapper>
                      <ImageIcon
                        width={30}
                        height={36}
                        icon={iconFaxDisabled}
                      />
                      <Text
                        fontStyle={FontStyle.ExTiny}
                        text={fileName}
                        color={light.gray100}
                      />
                    </InputFileWrapper>
                  )}

                  <ImageButton
                    icon={iconCloseXDark900}
                    width="20px"
                    height="20px"
                    imgWidth={8}
                    imgHeight={8}
                    borderWidth={2}
                    borderColor={light.gray400}
                    backColor={light.gray400}
                    activeColor={light.dark600}
                    hoveredColor={light.gray400}
                    disabledColor={light.dark700}
                    onClick={onRemove}
                  />
                </InputImageWrapper>
                {!isNewThread ? (
                  <ImageButton
                    disabled={disabled}
                    icon={iconSendTextBlue900}
                    tooltipText={t('messageInput.sendText')}
                    disabledIcon={iconSendFullGray}
                    width="32px"
                    height="34px"
                    imgWidth={22}
                    imgHeight={22}
                    backColor={light.transparent}
                    activeColor={light.transparent}
                    hoveredColor={light.transparent}
                    disabledColor={light.transparent}
                    onClick={onSend}
                  />
                ) : null}
              </InputImageRootWrapper>
            )}
          </InputWrapper>
          <CharacterCount count={count} />
        </div>
      </InputRootWrapper>
    )
  },
)

export default MessageInput
